import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Snackbar, IconButton, Slide, Grow } from '@mui/material';
import { 
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

interface AlertMessageProps {
  open: boolean;
  message: string;
  severity?: 'success' | 'info' | 'warning' | 'error';
  title?: string;
  autoHideDuration?: number;
  onClose: () => void;
}

export default function AlertMessage({
  open,
  message,
  severity = 'info',
  title,
  autoHideDuration = 6000,
  onClose
}: AlertMessageProps) {
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsOpen(false);
    onClose();
  };

  const getIcon = () => {
    switch (severity) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 24 }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 24 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 24 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 24 }} />;
      default:
        return <InfoIcon sx={{ fontSize: 24 }} />;
    }
  };

  const getGradient = () => {
    switch (severity) {
      case 'success':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'info':
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)';
      case 'error':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      default:
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    }
  };

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'left' } as any}
    >
      <Alert
        severity={severity}
        onClose={handleClose}
        icon={getIcon()}
        sx={{
          minWidth: 300,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          background: 'white',
          border: 'none',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: getGradient(),
          },
          '& .MuiAlert-icon': {
            color: severity === 'success' ? '#667eea' : 
                   severity === 'info' ? '#4facfe' :
                   severity === 'warning' ? '#fa709a' : '#f5576c',
            fontSize: 28,
          },
          '& .MuiAlert-message': {
            py: 0.5,
          },
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: {
              transform: 'translateX(100%)',
              opacity: 0,
            },
            to: {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleClose}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'rotate(90deg)',
                bgcolor: 'rgba(0,0,0,0.05)',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {title && (
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </AlertTitle>
        )}
        {message}
      </Alert>
    </Snackbar>
  );
}